import React from 'react';
import { useField, useFormikContext } from 'formik';
import type { FormField } from '../../interfaces';
import { CompanyInfoBox } from './CompanyInfoBox';
import { CompanyProvider } from './CompanyContext';
import type { Company } from '../../services/rtkApi';
import { CompanySelectorForm } from './CompanySelectorForm';
import { MANUAL_ORG_NUMBER } from '.';

export const CompanySelector = ({
    field,
    isReadOnly,
    isRequired = false,
}: {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
}) => {
    const { setFieldValue, getFieldMeta } = useFormikContext<Company>();
    const [formikFieldOrganisasjonsnummer] = useField<string>(
        'organisasjonsnummer',
    );
    const [formikFieldFirmanavn] = useField<string>(field.id);

    const meta = getFieldMeta<Company>(field.id);

    const syncWithFormik = (company: Company | null) => {
        if (!company) {
            setFieldValue(field.id, '');
            setFieldValue('organisasjonsnummer', '');
            return;
        }

        // For manual entries, always sync the name since it changes while org number stays "manuell"
        if (company.organisasjonsnummer === MANUAL_ORG_NUMBER) {
            setFieldValue(field.id, company.navn || '');
            setFieldValue('organisasjonsnummer', MANUAL_ORG_NUMBER);
            return;
        }

        // Guard to update only changed values for registry selections
        if (
            formikFieldOrganisasjonsnummer.value === company.organisasjonsnummer
        ) {
            return;
        }

        setFieldValue(field.id, company.navn || '');
        setFieldValue('organisasjonsnummer', company.organisasjonsnummer || '');
    };

    const error = meta.touched && Boolean(meta.error) ? meta.error : undefined;

    return (
        <CompanyProvider
            syncWithForm={syncWithFormik}
            defaultValue={formikFieldOrganisasjonsnummer.value}
        >
            <CompanySelectorForm
                error={error}
                field={field}
                isReadOnly={isReadOnly}
                isRequired={isRequired}
                defaultValue={formikFieldOrganisasjonsnummer.value}
                initialManualName={
                    formikFieldOrganisasjonsnummer.value === MANUAL_ORG_NUMBER
                        ? formikFieldFirmanavn.value
                        : undefined
                }
            />
            <CompanyInfoBox
                formikFirmanavn={formikFieldFirmanavn.value}
                formikOrganisasjonsnummer={formikFieldOrganisasjonsnummer.value}
            />
        </CompanyProvider>
    );
};
